import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Crown, Globe, Mail, Phone, Pencil, Calendar, Facebook, Instagram, Building2 } from "lucide-react";
import { PublicGallery } from "@/components/EntityEditShared";
import { EntityBadge } from "@/components/EntityBadge";
import { ClaimPageButton } from "@/components/ClaimPageButton";
import { EntityAffiliationsPanel } from "@/components/EntityAffiliationsPanel";
import { resolveAthletePhoto as photoFallback } from "@/lib/photoFallback";

function MiniStat({ value, label, tone = "muted" }) {
  const toneClass = {
    gold: "text-[hsl(var(--aito-gold))]",
    lagoon: "text-[hsl(var(--aito-lagoon))]",
    muted: "text-foreground",
  }[tone];
  return (
    <div className="text-center">
      <div className={`text-2xl font-bold font-display ${toneClass}`}>{value ?? "—"}</div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}

export default function FederationDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [fed, setFed] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [summary, setSummary] = useState(null); // { kpis, top_athletes, top_clubs }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/federations/${slug}`);
        if (cancelled) return;
        setFed(data);
        // Clubs de tutelle
        api.get(`/clubs?limit=200`).then((r) => {
          if (cancelled) return;
          setClubs((r.data?.items || []).filter((c) => c.federation_id === data.id));
        }).catch(() => {});
        // Liens entrants (athlètes/clubs validés)
        api.get(`/entity-links?target_type=federation&target_id=${data.id}&status=accepted&limit=200`)
          .then((r) => !cancelled && setIncoming(r.data?.items || []))
          .catch(() => {});
        // V11.4 — Récapitulatif KPI + top athletes + top clubs
        api.get(`/federations/${slug}/summary`)
          .then((r) => !cancelled && setSummary(r.data))
          .catch(() => {});
      } catch (e) {
        if (e?.response?.status === 404) navigate("/federations", { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, navigate]);

  if (loading) return <div className="max-w-5xl mx-auto py-8 px-4 space-y-4"><Skeleton className="h-48 w-full" /><Skeleton className="h-24 w-full" /></div>;
  if (!fed) return null;

  const isOwner = user && (user.id === fed.owner_user_id || (user.roles || []).some((r) => ["admin", "copf"].includes(r)));

  return (
    <div className="max-w-5xl mx-auto py-6 px-4 space-y-5" data-testid="federation-detail-page">
      <div className="relative h-48 rounded-xl overflow-hidden bg-gradient-to-br from-[hsl(var(--aito-black))] to-[hsl(var(--aito-gold)/0.6)]">
        {/* V11.13 — Cover réelle si disponible, sinon fallback bannière Fédération AITO.PF */}
        <img
          src={fed.cover_url || "/branding/cover-federation-default.png"}
          alt=""
          className="object-cover w-full h-full opacity-50"
          onError={(e) => { e.currentTarget.src = "/branding/cover-federation-default.png"; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3 flex-wrap">
          <div className="flex items-end gap-3 min-w-0">
            <div className="h-20 w-20 rounded-xl bg-background border-2 border-background shadow-lg flex items-center justify-center overflow-hidden shrink-0">
              {fed.logo_url ? <img src={fed.logo_url} alt={fed.name} className="object-cover w-full h-full" /> : <Crown className="h-9 w-9 text-muted-foreground" />}
            </div>
            <div className="text-white min-w-0">
              <h1 className="font-display text-3xl font-bold drop-shadow" data-testid="federation-name">{fed.name}</h1>
              {fed.short_motto && <p className="text-sm opacity-90 italic">« {fed.short_motto} »</p>}
              <div className="flex items-center gap-2 mt-1 flex-wrap text-xs">
                {fed.discipline && <Badge variant="secondary" className="capitalize">{fed.discipline}</Badge>}
                {fed.is_copf && <Badge className="bg-[hsl(var(--aito-gold))] text-black">COPF</Badge>}
                {fed.founded_year && <span className="flex items-center gap-0.5 opacity-90"><Calendar className="h-3 w-3" />Fondée en {fed.founded_year}</span>}
                {fed.president && <span className="opacity-90">Président·e : {fed.president}</span>}
              </div>
            </div>
          </div>
          {isOwner && (
            <Link to={`/mes-federations/${fed.slug || fed.id}`} data-testid="federation-cockpit-btn">
              <Button size="sm" variant="default" className="bg-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon-2))] text-black">
                <Crown className="h-3.5 w-3.5 mr-1.5" />Ouvrir mon cockpit
              </Button>
            </Link>
          )}
          {isOwner && (
            <Link to={`/federations/${fed.id}/edit`} data-testid="federation-edit-btn">
              <Button size="sm" variant="outline" className="border-[hsl(var(--aito-gold))]/60">
                <Pencil className="h-3.5 w-3.5 mr-1.5" />Modifier
              </Button>
            </Link>
          )}
          {user && !isOwner && (
            <ClaimPageButton entityType="federation" entityId={fed.id} entityName={fed.name} />
          )}
        </div>
      </div>

      {/* V11.4 — Récapitulatif KPI (§9) */}
      {summary?.kpis && (
        <Card className="p-4" data-testid="federation-kpi-recap">
          <div className="grid grid-cols-4 md:grid-cols-7 gap-3 divide-x divide-border">
            <MiniStat value={summary.kpis.clubs_count} label="Clubs" tone="lagoon" />
            <MiniStat value={summary.kpis.athletes_count} label="Athletes" tone="lagoon" />
            <MiniStat value={summary.kpis.coaches_count} label="Coachs" />
            <MiniStat value={summary.kpis.achievements_year_count} label={`Résultats ${summary.kpis.current_year}`} />
            <MiniStat value={summary.kpis.gold_count} label="Or" tone="gold" />
            <MiniStat value={summary.kpis.silver_count} label="Argent" />
            <MiniStat value={summary.kpis.bronze_count} label="Bronze" />
          </div>
          {summary.kpis.jp2027_selected_count > 0 && (
            <div className="mt-3 pt-3 border-t text-center text-sm">
              <Badge className="bg-[hsl(var(--aito-gold))] text-black">
                <Crown className="h-3 w-3 mr-1" />
                {summary.kpis.jp2027_selected_count} athlete{summary.kpis.jp2027_selected_count > 1 ? "s" : ""} SELECTED — Jeux du Pacifique 2027
              </Badge>
            </div>
          )}
        </Card>
      )}

      {/* V9.13 — Tabs masqués si vides + EntityBadge unifié */}
      {(() => {
        const athletes = summary?.top_athletes || [];
        const hasDescription = !!fed.description;
        const hasContact = fed.contact_email || fed.contact_phone || fed.website || fed.social_facebook || fed.social_instagram;
        const showOverview = hasDescription || hasContact;
        const hasClubs = clubs.length > 0 || (summary?.top_clubs || []).length > 0;
        const hasAthletes = athletes.length > 0;
        const hasGallery = (fed.gallery || []).length > 0;
        return (
          <Tabs defaultValue={showOverview ? "overview" : (hasClubs ? "clubs" : hasAthletes ? "athletes" : "gallery")}>
            <TabsList data-testid="federation-tabs">
              {showOverview && <TabsTrigger value="overview" data-testid="federation-tab-overview">Présentation</TabsTrigger>}
              {isOwner && <TabsTrigger value="affiliations" data-testid="federation-tab-affiliations">Affiliations</TabsTrigger>}
              {hasClubs && <TabsTrigger value="clubs" data-testid="federation-tab-clubs">Clubs de tutelle ({clubs.length})</TabsTrigger>}
              {hasAthletes && <TabsTrigger value="athletes" data-testid="federation-tab-athletes">Athlètes validés ({athletes.length})</TabsTrigger>}
              {hasGallery && <TabsTrigger value="gallery" data-testid="federation-tab-gallery">Galerie ({fed.gallery.length})</TabsTrigger>}
            </TabsList>

            {showOverview && (
              <TabsContent value="overview" className="mt-4 space-y-4">
                {hasDescription && (
                  <Card className="p-5">
                    <h2 className="font-display font-semibold mb-2">À propos</h2>
                    <p className="text-sm whitespace-pre-wrap text-muted-foreground">{fed.description}</p>
                  </Card>
                )}
                {hasContact && (
                  <Card className="p-5 space-y-2">
                    <h3 className="font-display font-semibold text-sm">Contact</h3>
                    {fed.contact_email && <a href={`mailto:${fed.contact_email}`} className="text-xs flex items-center gap-1.5 hover:underline"><Mail className="h-3.5 w-3.5 text-muted-foreground" />{fed.contact_email}</a>}
                    {fed.contact_phone && <p className="text-xs flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-muted-foreground" />{fed.contact_phone}</p>}
                    {fed.website && <a href={fed.website} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1.5 hover:underline"><Globe className="h-3.5 w-3.5 text-muted-foreground" />{fed.website}</a>}
                    {(fed.social_facebook || fed.social_instagram) && (
                      <div className="flex items-center gap-2 pt-1">
                        {fed.social_facebook && <a href={fed.social_facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="text-muted-foreground hover:text-foreground"><Facebook className="h-4 w-4" /></a>}
                        {fed.social_instagram && <a href={fed.social_instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="text-muted-foreground hover:text-foreground"><Instagram className="h-4 w-4" /></a>}
                      </div>
                    )}
                  </Card>
                )}
              </TabsContent>
            )}

            {isOwner && (
              <TabsContent value="affiliations" className="mt-4 space-y-4">
                <EntityAffiliationsPanel entityType="federation" entityId={fed.id} canManage={isOwner} />
              </TabsContent>
            )}

            {hasClubs && (
              <TabsContent value="clubs" className="mt-4">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {clubs.map((c) => (
                    <Link key={c.id} to={`/clubs/${c.slug}`} data-testid={`fed-club-${c.id}`}>
                      <Card className="p-3 flex items-center gap-2.5 hover:bg-accent transition-colors">
                        <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                          {c.logo_url ? <img src={c.logo_url} alt={c.name} className="object-cover w-full h-full rounded-lg" /> : <Building2 className="h-5 w-5 text-muted-foreground" />}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{c.name}</div>
                          <div className="text-[11px] text-muted-foreground truncate">{[c.commune, c.island].filter(Boolean).join(" · ") || c.discipline}</div>
                        </div>
                      </Card>
                    </Link>
                  ))}
                </div>
              </TabsContent>
            )}

            {hasAthletes && (
              <TabsContent value="athletes" className="mt-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="federation-athletes-grid">
                  {athletes.map((a) => (
                    <Link key={a.id} to={`/athletes/${a.slug}`} className="hover:opacity-80 transition" data-testid={`federation-athlete-${a.id}`}>
                      <Card className="p-2">
                        <div className="aspect-square rounded overflow-hidden bg-muted mb-1.5">
                          <img
                            src={a.photo_url || photoFallback(a)}
                            alt={a.full_name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.src = photoFallback(a); }}
                          />
                        </div>
                        <div className="text-xs font-medium truncate">{a.full_name}</div>
                        {a.main_discipline && <div className="text-[10px] text-muted-foreground truncate capitalize">{a.main_discipline}</div>}
                      </Card>
                    </Link>
                  ))}
                </div>
                {summary?.kpis?.athletes_count > athletes.length && (
                  <p className="text-xs text-muted-foreground italic text-center mt-3">
                    Aperçu de {athletes.length} athletes sur {summary.kpis.athletes_count} — les gestionnaires accèdent à la liste complète dans le cockpit.
                  </p>
                )}
              </TabsContent>
            )}

            {hasGallery && (
              <TabsContent value="gallery" className="mt-4">
                <Card className="p-5"><PublicGallery items={fed.gallery} /></Card>
              </TabsContent>
            )}
          </Tabs>
        );
      })()}
    </div>
  );
}
